import { gql, useQuery } from '@apollo/client';

import { CurrentUser } from 'models/User';
import { Project } from 'models/Project';
import Config from '../../models/Config';
import { ProjectStatusFilter } from './state';

interface GetCurrentUserData {
  currentUser: CurrentUser;
}

interface GetCurrentUserVars {
  organizationId: string;
}

const GET_CURRENT_USER = gql`
  query getCurrentUser($organizationId: ID!) {
    currentUser {
      __typename
      id
      name
      role(organizationId: $organizationId)
      organization(organizationId: $organizationId) {
        __typename
        id
        name
        isActive
        experiments
        owner {
          name
          email
        }
        projectFolders {
          id
          name
          subfolders {
            id
            name
          }
        }
        projectFoldersAllowed
        inTrial
      }
      favouriteProjects
      createdProjectFolder
      addedProjectToFolder
      completedProjectFolderTour
      seenProjectFolderVideo
      accessTo(organizationId: $organizationId) {
        viewUserActivities
        createProject
        viewKanban
        disableProject
        deleteProject
        canViewAssets
        manageProjectFolders
      }
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation UpdateProject($projectId: ID!, $isActive: Boolean) {
    updateProject(input: { projectId: $projectId, isActive: $isActive }) {
      project {
        __typename
        id
        isActive
      }
    }
  }
`;

export const useGetCurrentUserQuery = ({
  organizationId
}: {
  organizationId: string;
}) =>
  useQuery<GetCurrentUserData, GetCurrentUserVars>(GET_CURRENT_USER, {
    variables: {
      organizationId
    },
    fetchPolicy: 'network-only'
  });

export interface GetUserProjectsData {
  config: Config;
  currentUser: {
    id: string;
    userProjectsCount: number;
    userProjects: Project[];
  };
}

export interface GetUserProjectsVars {
  organizationId: string;
  offset: number;
  limit: number;
  filter?: {
    name: string | null;
    favourites?: string[];
    projectFolderId?: string | null;
  };
  sortBy?: string;
  projectStatusFilter: ProjectStatusFilter;
}

export const GET_USER_PROJECTS = gql`
  query getUserProjects(
    $organizationId: ID!
    $offset: Int!
    $limit: Int!
    $filter: ProjectFilter
    $sortBy: String!
    $projectStatusFilter: String!
  ) {
    config {
      pusherApiKey
      pusherChannelAuthEndpoint
    }
    currentUser {
      __typename
      id
      userProjectsCount(
        organizationId: $organizationId
        filter: $filter
        sortBy: $sortBy
        projectStatusFilter: $projectStatusFilter
      )
      userProjects(
        organizationId: $organizationId
        offset: $offset
        limit: $limit
        filter: $filter
        sortBy: $sortBy
        projectStatusFilter: $projectStatusFilter
      ) {
        __typename
        id
        name
        sites {
          id
          url
        }
        pusherChannelName
        isActive
        devurl
        hasCompletedChecklist
        coverScreenshot
        coverUpdateRequestedAt
        updatedAt
        triageTasksCount
        organizationId
        projectFolderId
        projectFolderName
        assets {
          id
        }
      }
    }
  }
`;

interface GetAllUserProjectsCountData {
  currentUser: {
    id: string;
    userProjectsCount: number;
  };
}

interface GetAllUserProjectsCountVars {
  organizationId: string;
  sortBy: string;
  projectStatusFilter: ProjectStatusFilter;
}

export const GET_ALL_USER_PROJECTS_COUNT = gql`
  query getAllUserProjectsCount(
    $organizationId: ID!
    $sortBy: String!
    $projectStatusFilter: String!
  ) {
    currentUser {
      __typename
      id
      userProjectsCount(
        organizationId: $organizationId
        sortBy: $sortBy
        projectStatusFilter: $projectStatusFilter
      )
    }
  }
`;

export const useGetAllUserProjectsCountQuery = ({
  organizationId,
  skip
}: {
  organizationId: string;
  skip: boolean;
}) =>
  useQuery<GetAllUserProjectsCountData, GetAllUserProjectsCountVars>(
    GET_ALL_USER_PROJECTS_COUNT,
    {
      variables: {
        organizationId,
        sortBy: 'updated_at',
        projectStatusFilter: ProjectStatusFilter.ALL
      },
      skip
    }
  );

export const GET_UNREAD_COUNT = gql`
  query getUnreadCount($projectId: ID!) {
    currentUser {
      userProject(id: $projectId) {
        __typename
        id
        numberOfUnreadComments
        numberOfUnreadMentions
        openTasksCount
      }
    }
  }
`;
